/**
 * Shared T-SQL completion (SQL Server 2019 and later) for the SQL editors. SqlEditorAdapter returns
 * createSqlCompletionProvider() by default; an adapter enriches it for its place of use:
 *
 *   getCompletionProvider() {
 *       return createSqlCompletionProvider({
 *           items: () => this.parameters.map(p => ({label: '@' + p.name, kind: 'variable', detail: p.type})),
 *       });
 *   }
 *
 * Keywords, types and function names are inserted in the case the user is typing (lowercase when nothing was
 * typed yet, as the SQL of this project is written), signatures as snippets with one placeholder per argument.
 * The ETL engine's built-in `@variables`, `{pseudo-variables}` and `{{macros}}` (p_ETL_EXEC_THREAD, p_CUSTOM_QUERY)
 * are offered too; a place where the SQL runs differently switches them off one by one (`internalVariables`,
 * `pseudoVariables`, `macros`) or passes its own placeholder list (`pseudoVariables: [...]`).
 */
import { CodeCompletionContext, CodeCompletionItem, CodeCompletionProvider } from "./codeCompletion";
import { createSchemaCompletion, SqlSchemaOptions } from "./schemaCompletion";

export type SqlKeywordCase = 'auto' | 'upper' | 'lower';

export type SqlCompletionGroup = 'keywords' | 'functions' | 'variables' | 'internal' | 'pseudo' | 'macros' | 'types' | 'snippets';

/** [name, description] of a placeholder the application replaces in the SQL text before it runs */
export type SqlPseudoVariable = [name: string, description: string];

export type SqlCompletionOptions = {
	/** case of inserted keywords, types and function names: follow the typed text ('auto', default; lowercase when nothing typed), or force one */
	keywordCase?: SqlKeywordCase,
	/** insert function signatures as snippets with argument placeholders (default true); false inserts the bare name */
	signatures?: boolean,
	/** built-in groups to offer (default all) */
	groups?: SqlCompletionGroup[],
	/** offer the ETL engine's `@variables` (group internal; default true) */
	internalVariables?: boolean,
	/** offer `{pseudo-variables}` (group pseudo; default true): the engine's list, or the list of the place of use */
	pseudoVariables?: boolean | SqlPseudoVariable[],
	/** offer the `{{macros}}` of the ETL preprocessing (group macros; default true) */
	macros?: boolean,
	/** additional items (parameters, columns, pseudo-variables...); offered before the built-in ones */
	items?: CodeCompletionItem[] | ((context: CodeCompletionContext) => CodeCompletionItem[] | Promise<CodeCompletionItem[]>),
	/** trigger characters besides '@', e.g. '.' when columns are offered */
	triggerCharacters?: string[],
	/** see CodeCompletionProvider.documentWords */
	documentWords?: boolean,
	/**
	 * database objects of the static catalogue (see schemaCompletion.ts): offered after `{db_x}.dbo.`, `dbo.` and
	 * `alias.`, and without a prefix those of `schema.database` (default: the customer data database); false switches them off
	 */
	schema?: false | SqlSchemaOptions,
};

/** [signature, description, snippet]; the snippet is derived from the signature when missing (optional `[, arg]` parts are left out) */
export type SqlFunctionDef = [signature: string, description: string, snippet?: string];

export const SQL_FUNCTIONS: Record<string, SqlFunctionDef[]> = {
	'Aggregate': [
		['AVG(expression)', 'Average of the values in the group; nulls are ignored.'],
		['COUNT(expression)', 'Number of rows in the group; COUNT(*) counts all rows, COUNT(column) only the non-null values.'],
		['COUNT_BIG(expression)', 'Like COUNT, returns bigint.'],
		['MAX(expression)', 'Maximum value in the group.'],
		['MIN(expression)', 'Minimum value in the group.'],
		['SUM(expression)', 'Sum of the values in the group; nulls are ignored.'],
		['STRING_AGG(expression, separator)', 'Concatenates the values of the group with the separator; order them with WITHIN GROUP (ORDER BY ...).'],
		['STDEV(expression)', 'Standard deviation of the values in the group (sample).'],
		['STDEVP(expression)', 'Standard deviation of the values in the group (population).'],
		['VAR(expression)', 'Variance of the values in the group (sample).'],
		['VARP(expression)', 'Variance of the values in the group (population).'],
		['GROUPING(column_expression)', '1 when the row is a summary produced by ROLLUP, CUBE or GROUPING SETS, else 0.'],
		['GROUPING_ID(column_expression [, ...])', 'Bitmap of the GROUPING values of the listed columns.'],
		['CHECKSUM_AGG(expression)', 'Checksum of the values in the group; changes when any value changes.'],
		['APPROX_COUNT_DISTINCT(expression)', 'Approximate number of distinct values; far cheaper than COUNT(DISTINCT ...) on big sets.'],
	],
	'String': [
		['ASCII(character_expression)', 'ASCII code of the leftmost character.'],
		['CHAR(integer_expression)', 'Character of an ASCII code, e.g. CHAR(13) + CHAR(10) for a line break.'],
		['CHARINDEX(expression_to_find, expression_to_search [, start_location])', 'Position of the first occurrence of a substring (1-based, 0 when not found).'],
		['CONCAT(string_value1, string_value2 [, ...])', 'Joins the values into one string; nulls become empty strings.'],
		['CONCAT_WS(separator, argument1, argument2 [, ...])', 'Joins the values with the separator, skipping nulls.'],
		['DATALENGTH(expression)', 'Number of bytes of the value.'],
		['DIFFERENCE(character_expression1, character_expression2)', 'Similarity of the SOUNDEX values of two strings (0 to 4).'],
		['FORMAT(value, format [, culture])', 'Formats a number or date with a .NET format string, e.g. FORMAT(d, \'dd.MM.yyyy\'); slow on large sets, prefer CONVERT.'],
		['LEFT(character_expression, integer_expression)', 'Leftmost characters of the string.'],
		['LEN(string_expression)', 'Number of characters, trailing spaces excluded.'],
		['LOWER(character_expression)', 'Converts the string to lowercase.'],
		['LTRIM(character_expression)', 'Removes leading spaces.'],
		['NCHAR(integer_expression)', 'Unicode character of a code.'],
		['PATINDEX(\'%pattern%\', expression)', 'Position of the first occurrence of a pattern (LIKE wildcards), 0 when not found.'],
		['QUOTENAME(character_string [, quote_character])', 'Delimits an identifier, [name] by default.'],
		['REPLACE(string_expression, string_pattern, string_replacement)', 'Replaces all occurrences of a substring.'],
		['REPLICATE(string_expression, integer_expression)', 'Repeats the string the given number of times.'],
		['REVERSE(string_expression)', 'Reverses the string.'],
		['RIGHT(character_expression, integer_expression)', 'Rightmost characters of the string.'],
		['RTRIM(character_expression)', 'Removes trailing spaces.'],
		['SOUNDEX(character_expression)', 'Four-character code of how the string sounds.'],
		['SPACE(integer_expression)', 'String of the given number of spaces.'],
		['STR(float_expression [, length [, decimal]])', 'Converts a number to a right-aligned string.'],
		['STRING_ESCAPE(text, \'json\')', 'Escapes the special characters of the text (only the json type is supported).'],
		['STRING_SPLIT(string, separator)', 'Table of the substrings (column value); use in FROM or CROSS APPLY.'],
		['STUFF(character_expression, start, length, replace_with)', 'Deletes a part of the string and inserts another one at the same position.'],
		['SUBSTRING(expression, start, length)', 'Part of the string (start is 1-based).'],
		['TRANSLATE(input_string, characters, translations)', 'Replaces the characters one by one with the ones at the same position in translations.'],
		['TRIM(string)', 'Removes leading and trailing spaces; TRIM(characters FROM string) removes the given characters.'],
		['UNICODE(ncharacter_expression)', 'Unicode code of the first character.'],
		['UPPER(character_expression)', 'Converts the string to uppercase.'],
	],
	'Date and time': [
		['GETDATE()', 'Current date and time of the server (datetime).'],
		['GETUTCDATE()', 'Current UTC date and time (datetime).'],
		['SYSDATETIME()', 'Current date and time of the server (datetime2(7)).'],
		['SYSUTCDATETIME()', 'Current UTC date and time (datetime2(7)).'],
		['SYSDATETIMEOFFSET()', 'Current date and time with the time zone offset of the server.'],
		['CURRENT_TIMESTAMP', 'Current date and time of the server (datetime), same as GETDATE().', 'CURRENT_TIMESTAMP'],
		['DATEADD(datepart, number, date)', 'Adds an interval to a date; datepart: year, quarter, month, week, day, hour, minute, second, millisecond.'],
		['DATEDIFF(datepart, startdate, enddate)', 'Number of datepart boundaries crossed between two dates (int).'],
		['DATEDIFF_BIG(datepart, startdate, enddate)', 'Like DATEDIFF, returns bigint.'],
		['DATENAME(datepart, date)', 'Name of the datepart as a string, e.g. the month or weekday name.'],
		['DATEPART(datepart, date)', 'Datepart as an integer, e.g. DATEPART(quarter, d).'],
		['DAY(date)', 'Day of the month.'],
		['MONTH(date)', 'Month of the date.'],
		['YEAR(date)', 'Year of the date.'],
		['DATEFROMPARTS(year, month, day)', 'Date from its parts.'],
		['DATETIMEFROMPARTS(year, month, day, hour, minute, seconds, milliseconds)', 'Datetime from its parts.'],
		['DATETIME2FROMPARTS(year, month, day, hour, minute, seconds, fractions, precision)', 'Datetime2 from its parts.'],
		['TIMEFROMPARTS(hour, minute, seconds, fractions, precision)', 'Time from its parts.'],
		['EOMONTH(start_date [, month_to_add])', 'Last day of the month, optionally shifted by months.'],
		['ISDATE(expression)', '1 when the expression is a valid date, time or datetime.'],
		['SWITCHOFFSET(datetimeoffset_expression, timezoneoffset)', 'Same moment expressed in another time zone offset, e.g. \'+01:00\'.'],
		['TODATETIMEOFFSET(expression, timezoneoffset)', 'Datetime2 to datetimeoffset with the given offset.'],
	],
	'Conversion': [
		['CAST(expression AS data_type)', 'Converts the value to the data type; fails on an invalid value.', 'CAST(${1:expression} AS ${2:data_type})'],
		['CONVERT(data_type, expression [, style])', 'Converts the value with an optional style, e.g. 104 = dd.mm.yyyy, 112 = yyyymmdd, 120 = yyyy-mm-dd hh:mi:ss.'],
		['TRY_CAST(expression AS data_type)', 'Like CAST, returns NULL instead of failing.', 'TRY_CAST(${1:expression} AS ${2:data_type})'],
		['TRY_CONVERT(data_type, expression [, style])', 'Like CONVERT, returns NULL instead of failing.'],
		['PARSE(string_value AS data_type [USING culture])', 'Parses a string with .NET culture rules; slow, prefer CONVERT.', 'PARSE(${1:string_value} AS ${2:data_type})'],
		['TRY_PARSE(string_value AS data_type [USING culture])', 'Like PARSE, returns NULL instead of failing.', 'TRY_PARSE(${1:string_value} AS ${2:data_type})'],
	],
	'Logical': [
		['COALESCE(expression1, expression2 [, ...])', 'First non-null argument.'],
		['ISNULL(check_expression, replacement_value)', 'Replacement when the value is null; the result has the type of the first argument.'],
		['NULLIF(expression1, expression2)', 'NULL when both expressions are equal, else the first one.'],
		['IIF(boolean_expression, true_value, false_value)', 'Shorthand for CASE WHEN ... THEN ... ELSE ... END.'],
		['CHOOSE(index, value1, value2 [, ...])', 'Value at the 1-based index.'],
	],
	'Mathematical': [
		['ABS(numeric_expression)', 'Absolute value.'],
		['CEILING(numeric_expression)', 'Smallest integer greater than or equal to the value.'],
		['FLOOR(numeric_expression)', 'Largest integer less than or equal to the value.'],
		['ROUND(numeric_expression, length [, function])', 'Rounds to the given number of decimal places; function 1 truncates instead.'],
		['EXP(float_expression)', 'Exponential value.'],
		['LOG(float_expression [, base])', 'Natural logarithm, or the logarithm of the given base.'],
		['LOG10(float_expression)', 'Base-10 logarithm.'],
		['POWER(float_expression, y)', 'Value raised to the power y.'],
		['SQRT(float_expression)', 'Square root.'],
		['SQUARE(float_expression)', 'Square of the value.'],
		['SIGN(numeric_expression)', '-1, 0 or 1 by the sign of the value.'],
		['PI()', 'Constant value of pi.'],
		['RAND([seed])', 'Pseudo-random float between 0 and 1; one value per query, not per row.'],
		['DEGREES(numeric_expression)', 'Radians to degrees.'],
		['RADIANS(numeric_expression)', 'Degrees to radians.'],
		['SIN(float_expression)', 'Sine.'],
		['COS(float_expression)', 'Cosine.'],
		['TAN(float_expression)', 'Tangent.'],
		['ASIN(float_expression)', 'Arcsine.'],
		['ACOS(float_expression)', 'Arccosine.'],
		['ATAN(float_expression)', 'Arctangent.'],
		['ATN2(float_expression1, float_expression2)', 'Angle in radians between the positive x-axis and the point (y, x).'],
		['COT(float_expression)', 'Cotangent.'],
	],
	'Ranking and window': [
		['ROW_NUMBER() OVER (ORDER BY ...)', 'Sequential number of the row within its partition.', 'ROW_NUMBER() OVER (ORDER BY ${1:order})'],
		['RANK() OVER (ORDER BY ...)', 'Rank of the row within its partition; ties share the rank and leave gaps.', 'RANK() OVER (ORDER BY ${1:order})'],
		['DENSE_RANK() OVER (ORDER BY ...)', 'Rank of the row within its partition without gaps.', 'DENSE_RANK() OVER (ORDER BY ${1:order})'],
		['NTILE(integer_expression) OVER (ORDER BY ...)', 'Number of the group the row falls into when the partition is split into the given number of groups.', 'NTILE(${1:groups}) OVER (ORDER BY ${2:order})'],
		['LAG(scalar_expression [, offset [, default]]) OVER (ORDER BY ...)', 'Value of a previous row of the partition without a self join.', 'LAG(${1:expression}, ${2:1}) OVER (ORDER BY ${3:order})'],
		['LEAD(scalar_expression [, offset [, default]]) OVER (ORDER BY ...)', 'Value of a following row of the partition without a self join.', 'LEAD(${1:expression}, ${2:1}) OVER (ORDER BY ${3:order})'],
		['FIRST_VALUE(scalar_expression) OVER (ORDER BY ...)', 'Value of the first row of the window frame.', 'FIRST_VALUE(${1:expression}) OVER (ORDER BY ${2:order})'],
		['LAST_VALUE(scalar_expression) OVER (ORDER BY ...)', 'Value of the last row of the window frame; add ROWS BETWEEN ... AND UNBOUNDED FOLLOWING for the whole partition.', 'LAST_VALUE(${1:expression}) OVER (ORDER BY ${2:order} ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING)'],
		['CUME_DIST() OVER (ORDER BY ...)', 'Cumulative distribution of the value within the partition (0 to 1).', 'CUME_DIST() OVER (ORDER BY ${1:order})'],
		['PERCENT_RANK() OVER (ORDER BY ...)', 'Relative rank of the row within the partition (0 to 1).', 'PERCENT_RANK() OVER (ORDER BY ${1:order})'],
		['PERCENTILE_CONT(numeric_literal) WITHIN GROUP (ORDER BY ...) OVER (...)', 'Interpolated percentile of the values, e.g. 0.5 for the median.', 'PERCENTILE_CONT(${1:0.5}) WITHIN GROUP (ORDER BY ${2:order}) OVER ()'],
		['PERCENTILE_DISC(numeric_literal) WITHIN GROUP (ORDER BY ...) OVER (...)', 'Percentile picked from the existing values.', 'PERCENTILE_DISC(${1:0.5}) WITHIN GROUP (ORDER BY ${2:order}) OVER ()'],
	],
	'JSON': [
		['ISJSON(expression)', '1 when the string is valid JSON.'],
		['JSON_VALUE(expression, path)', 'Scalar value at the path, e.g. JSON_VALUE(doc, \'$.customer.name\').'],
		['JSON_QUERY(expression [, path])', 'Object or array at the path as a JSON string.'],
		['JSON_MODIFY(expression, path, new_value)', 'JSON with the value at the path updated, inserted or (NULL) deleted.'],
		['OPENJSON(json_expression [, path])', 'Table of the JSON elements; use in FROM or CROSS APPLY, with WITH (column type \'path\', ...) for a typed schema.'],
	],
	'System': [
		['SCOPE_IDENTITY()', 'Last identity value inserted in the current scope.'],
		['IDENT_CURRENT(\'table_name\')', 'Last identity value generated for the table in any session.'],
		['NEWID()', 'New uniqueidentifier.'],
		['OBJECT_ID(\'object_name\' [, \'object_type\'])', 'Id of the object, NULL when it does not exist, e.g. IF OBJECT_ID(\'tempdb..#t\') IS NOT NULL.'],
		['OBJECT_NAME(object_id [, database_id])', 'Name of the object.'],
		['OBJECT_SCHEMA_NAME(object_id [, database_id])', 'Schema name of the object.'],
		['SCHEMA_NAME([schema_id])', 'Name of the schema, the default schema of the user without an argument.'],
		['SCHEMA_ID(\'schema_name\')', 'Id of the schema.'],
		['DB_NAME([database_id])', 'Name of the database, the current one without an argument.'],
		['DB_ID([\'database_name\'])', 'Id of the database.'],
		['COL_NAME(table_id, column_id)', 'Name of the column.'],
		['COL_LENGTH(\'table\', \'column\')', 'Defined length of the column in bytes.'],
		['COLUMNPROPERTY(id, column, property)', 'Property of the column, e.g. \'IsIdentity\', \'Precision\', \'Scale\'.'],
		['OBJECTPROPERTY(id, property)', 'Property of the object, e.g. \'IsTable\', \'IsView\', \'IsProcedure\'.'],
		['TYPE_NAME(type_id)', 'Name of the data type.'],
		['SUSER_SNAME([server_user_sid])', 'Login name of the current session.'],
		['USER_NAME([id])', 'Database user name.'],
		['ORIGINAL_LOGIN()', 'Login that connected, even after EXECUTE AS.'],
		['HOST_NAME()', 'Workstation name of the client.'],
		['APP_NAME()', 'Application name of the session.'],
		['CONNECTIONPROPERTY(property)', 'Property of the connection, e.g. \'client_net_address\', \'protocol_type\'.'],
		['SESSION_CONTEXT(N\'key\')', 'Value stored in the session context by sp_set_session_context.'],
		['SERVERPROPERTY(\'propertyname\')', 'Property of the server, e.g. \'ProductVersion\', \'Edition\', \'MachineName\'.'],
		['DATABASEPROPERTYEX(\'database\', \'property\')', 'Property of the database, e.g. \'Status\', \'Recovery\', \'Collation\'.'],
		['ERROR_MESSAGE()', 'Message of the error that ran the CATCH block.'],
		['ERROR_NUMBER()', 'Number of the error that ran the CATCH block.'],
		['ERROR_LINE()', 'Line of the error that ran the CATCH block.'],
		['ERROR_PROCEDURE()', 'Procedure or trigger of the error that ran the CATCH block.'],
		['ERROR_SEVERITY()', 'Severity of the error that ran the CATCH block.'],
		['ERROR_STATE()', 'State of the error that ran the CATCH block.'],
		['XACT_STATE()', '1 = active committable transaction, -1 = uncommittable, 0 = none.'],
		['HASHBYTES(\'algorithm\', input)', 'Hash of the input; algorithms MD5, SHA1, SHA2_256, SHA2_512.', 'HASHBYTES(\'${1:SHA2_256}\', ${2:input})'],
		['CHECKSUM(expression [, ...])', 'Checksum of the values, CHECKSUM(*) of the whole row.'],
		['BINARY_CHECKSUM(expression [, ...])', 'Binary checksum of the values; case-sensitive unlike CHECKSUM.'],
		['ISNUMERIC(expression)', '1 when the expression can be converted to a numeric type.'],
		['COMPRESS(expression)', 'GZIP-compressed varbinary of the value.'],
		['DECOMPRESS(expression)', 'Decompressed varbinary of a COMPRESS result; CAST it to the original type.'],
		['FORMATMESSAGE(msg_string, param [, ...])', 'Message with %s and %d placeholders replaced by the parameters.'],
		['PARSENAME(\'object_name\', object_piece)', 'Part of a four-part name: 1 = object, 2 = schema, 3 = database, 4 = server.'],
		['ROWCOUNT_BIG()', 'Number of rows affected by the last statement (bigint).'],
		['STATS_DATE(object_id, stats_id)', 'Date of the last update of the statistics.'],
		['OPENQUERY(linked_server, \'query\')', 'Result of a query executed on a linked server.'],
	],
};

export const SQL_KEYWORDS: string[] = [
	'SELECT', 'DISTINCT', 'TOP', 'FROM', 'WHERE', 'GROUP BY', 'HAVING', 'ORDER BY', 'ASC', 'DESC',
	'JOIN', 'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN', 'CROSS JOIN', 'ON', 'CROSS APPLY', 'OUTER APPLY',
	'UNION', 'UNION ALL', 'EXCEPT', 'INTERSECT', 'AS', 'AND', 'OR', 'NOT', 'IN', 'NOT IN', 'EXISTS', 'NOT EXISTS',
	'BETWEEN', 'LIKE', 'IS NULL', 'IS NOT NULL', 'NULL', 'ANY', 'ALL', 'SOME', 'ESCAPE', 'COLLATE',
	'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'OVER', 'PARTITION BY', 'ROWS BETWEEN', 'UNBOUNDED PRECEDING', 'CURRENT ROW',
	'OFFSET', 'FETCH NEXT', 'WITH', 'PIVOT', 'UNPIVOT', 'TABLESAMPLE', 'OPTION', 'FOR JSON PATH', 'FOR XML PATH',
	'INSERT INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE', 'MERGE', 'OUTPUT', 'INTO', 'TRUNCATE TABLE',
	'DECLARE', 'BEGIN', 'IF', 'WHILE', 'BREAK', 'CONTINUE', 'RETURN', 'BEGIN TRY', 'END TRY', 'BEGIN CATCH', 'END CATCH',
	'THROW', 'RAISERROR', 'PRINT', 'EXEC', 'EXECUTE', 'GO', 'USE', 'WAITFOR', 'SET NOCOUNT ON',
	'CREATE TABLE', 'CREATE VIEW', 'CREATE PROCEDURE', 'CREATE FUNCTION', 'CREATE INDEX', 'CREATE OR ALTER',
	'ALTER TABLE', 'ALTER VIEW', 'ALTER PROCEDURE', 'ALTER FUNCTION',
	'DROP TABLE', 'DROP TABLE IF EXISTS', 'DROP VIEW', 'DROP PROCEDURE', 'DROP FUNCTION', 'DROP INDEX',
	'ADD', 'CONSTRAINT', 'PRIMARY KEY', 'FOREIGN KEY', 'REFERENCES', 'UNIQUE', 'DEFAULT', 'IDENTITY', 'NOT NULL',
	'CLUSTERED', 'NONCLUSTERED', 'INDEX', 'TABLE', 'VIEW', 'PROCEDURE', 'FUNCTION', 'TRIGGER', 'RETURNS',
	'BEGIN TRANSACTION', 'COMMIT', 'ROLLBACK', 'TRANSACTION',
];

export const SQL_TYPES: string[] = [
	'int', 'bigint', 'smallint', 'tinyint', 'bit', 'decimal', 'numeric', 'float', 'real', 'money', 'smallmoney',
	'date', 'datetime', 'datetime2', 'datetimeoffset', 'smalldatetime', 'time',
	'char', 'varchar', 'nchar', 'nvarchar', 'binary', 'varbinary', 'uniqueidentifier', 'xml', 'sql_variant', 'table',
];

/** [name, description] */
export const SQL_VARIABLES: [string, string][] = [
	['@@ROWCOUNT', 'Number of rows affected by the last statement.'],
	['@@ERROR', 'Error number of the last statement, 0 when it succeeded.'],
	['@@IDENTITY', 'Last identity value inserted in the session, in any scope; prefer SCOPE_IDENTITY().'],
	['@@TRANCOUNT', 'Number of active transactions of the session.'],
	['@@SPID', 'Session id of the current connection.'],
	['@@SERVERNAME', 'Name of the local server.'],
	['@@VERSION', 'Version, processor and OS of the server.'],
	['@@DATEFIRST', 'First day of the week (1 = Monday, 7 = Sunday).'],
	['@@FETCH_STATUS', 'Status of the last FETCH of a cursor, 0 when a row was fetched.'],
	['@@PROCID', 'Object id of the current procedure or function.'],
	['@@NESTLEVEL', 'Nesting level of the current procedure execution.'],
];

/** [name, type, description]: variables the ETL engine declares before the thread's SQL runs (p_ETL_EXEC_THREAD header) */
export const SQL_INTERNAL_VARIABLES: [string, string, string][] = [
	['@err', 'int', 'Error code, 0 by default.'],
	['@rowcount', 'int', 'Number of processed rows.'],
	['@id_call', 'int', 'Id of the call.'],
	['@call', 'varchar(1024)', 'Text description of the call.'],
	['@id_audit', 'int', 'Unique number of the ETL run.'],
	['@dr', 'datetime', 'Current date and time.'],
	['@accounting', 'int', 'Id of the company (accounting).'],
	['@username', 'nvarchar(50)', 'Logged-in user.'],
	['@user', 'nvarchar(50)', 'Alias of @username.'],
	['@loginname', 'nvarchar(50)', 'Login name of the user.'],
	['@thread_name', 'nvarchar(50)', 'Full name of the ETL thread.'],
	['@module', 'char(1)', 'Module type of the thread: F, I or A.'],
];

/** placeholders the ETL engine replaces in the SQL text before it runs */
export const SQL_PSEUDO_VARIABLES: SqlPseudoVariable[] = [
	['{db}', 'Name of the customer (data) database.'],
	['{db_master}', 'Database with the report definitions.'],
	['{db_super}', 'Database with the company parameters and users.'],
	['{db_etl}', 'Database of the ETL engine (threads, parameters, files, jobs).'],
	['{accounting}', 'Number of the company (accounting).'],
	['{run_id}', 'Random number of the run, for global temp tables (##).'],
	['{etl_path}', 'Physical path of the directory with the files.'],
];

/** placeholders DeploymentSqlRunner replaces in deployment threads (which of them depends on the thread type) */
export const SQL_DEPLOYMENT_PSEUDO_VARIABLES: SqlPseudoVariable[] = [
	['{db_super}', 'Database with the company parameters and users.'],
	['{db_master}', 'Database with the report definitions (master and data threads).'],
	['{db_data}', 'Customer (data) database (data threads).'],
	['{db_mdm}', 'MDM database of the company (data threads).'],
	['{mdm_table}', 'MDM table of the company (data threads).'],
	['{mdm_view}', 'MDM view derived from the MDM database (data threads).'],
	['{accounting}', 'Number of the company (data threads).'],
	['{run_id}', 'Random number of the run, for global temp tables (##).'],
];

/** [label, signature, description, snippet]: macros of the ETL preprocessing (p_ETL_EVAL_MACROS), evaluated before the SQL runs */
export const SQL_MACROS: [string, string, string, string][] = [
	['{{include}}', '{{include "thread_name" [accounting]}}', 'Inserts the code of another thread at this place.', '{{include "${1:thread_name}"}}'],
	['{{include_once}}', '{{include_once "thread_name"}}', 'Inserts the code of another thread unless it was already included.', '{{include_once "${1:thread_name}"}}'],
	['{{foreach}}', '{{foreach "sql" ["init_sql"]}} ... {{/foreach}}', 'Repeats the block for every row of the query; column values are available as {COLUMN_NAME}.', '{{foreach "${1:select ...}"}}\n\t$0\n{{/foreach}}'],
	['{{map}}', '{{map "sql" "separator"}}', 'Like foreach, joins the results with the separator.', '{{map "${1:select ...}" "${2:, }"}}'],
	['{{if}}', '{{if "condition"}} ... {{/if}}', 'Inserts the block only when the SQL condition is true.', '{{if "${1:condition}"}}\n\t$0\n{{/if}}'],
	['{{define}}', '{{define name "sql"}}', 'Defines a macro variable usable as {name}.', '{{define ${1:name} "${2:select ...}"}}'],
	['{{exec}}', '{{exec}} ... {{/exec}}', 'Runs the SQL before the thread; global temp tables (##name{run_id}) stay available to the following macros.', '{{exec}}\n\t$0\n{{/exec}}'],
];

/** [label, description, snippet] */
export const SQL_SNIPPETS: [string, string, string][] = [
	['CASE WHEN ... THEN ... ELSE ... END', 'Searched CASE expression.', 'CASE WHEN ${1:condition} THEN ${2:result} ELSE ${3:result} END'],
	['SELECT ... FROM ... WHERE', 'Query skeleton.', 'SELECT ${1:*}\nFROM ${2:table}\nWHERE ${3:condition}'],
	['INSERT INTO ... VALUES', 'Insert of listed values.', 'INSERT INTO ${1:table} (${2:columns})\nVALUES (${3:values})'],
	['UPDATE ... SET ... WHERE', 'Update statement.', 'UPDATE ${1:table}\nSET ${2:column} = ${3:value}\nWHERE ${4:condition}'],
	['DELETE FROM ... WHERE', 'Delete statement.', 'DELETE FROM ${1:table}\nWHERE ${2:condition}'],
	['WITH cte AS (...)', 'Common table expression.', 'WITH ${1:cte} AS (\n\t${2:select}\n)\nSELECT *\nFROM ${1:cte}'],
	['IF EXISTS (...) BEGIN ... END', 'Conditional block on a subquery.', 'IF EXISTS (SELECT 1 FROM ${1:table} WHERE ${2:condition})\nBEGIN\n\t$0\nEND'],
	['BEGIN TRY ... END TRY BEGIN CATCH ... END CATCH', 'Error handling block.', 'BEGIN TRY\n\t$1\nEND TRY\nBEGIN CATCH\n\t$0\nEND CATCH'],
	['BEGIN TRANSACTION ... COMMIT', 'Transaction block.', 'BEGIN TRANSACTION\n\t$0\nCOMMIT TRANSACTION'],
	['DECLARE @name type = value', 'Variable declaration.', 'DECLARE @${1:name} ${2:int} = ${3:value}'],
	['OFFSET ... ROWS FETCH NEXT ... ROWS ONLY', 'Paging of an ordered result.', 'OFFSET ${1:0} ROWS FETCH NEXT ${2:100} ROWS ONLY'],
	['ROWS BETWEEN ... PRECEDING AND CURRENT ROW', 'Window frame of the preceding rows.', 'ROWS BETWEEN ${1:n} PRECEDING AND CURRENT ROW'],
];

const SORT_PREFIX: Record<SqlCompletionGroup | 'extra', string> = {
	extra: '0',
	keywords: '1',
	functions: '2',
	internal: '3',
	pseudo: '3',
	macros: '3',
	variables: '4',
	types: '5',
	snippets: '6',
};

const ALL_GROUPS: SqlCompletionGroup[] = ['keywords', 'functions', 'variables', 'internal', 'pseudo', 'macros', 'types', 'snippets'];

type ResolvedCase = 'upper' | 'lower';

function resolveCase(mode: SqlKeywordCase, word: string): ResolvedCase {
	if (mode === 'upper' || mode === 'lower') return mode;
	return /[A-Z]/.test(word) ? 'upper' : 'lower';
}

/** Applies the case to the text outside snippet placeholders, so `${1:datepart}` keeps its hint. */
export function applySqlCase(text: string, letterCase: ResolvedCase): string {
	return text
		.split(/(\$\{\d+:[^}]*\}|\$\d+)/)
		.map((part, i) => i % 2 ? part : (letterCase === 'upper' ? part.toUpperCase() : part.toLowerCase()))
		.join('');
}

/** Name and required arguments of a signature like `ROUND(numeric_expression, length [, function])`; args is null for a niladic function. */
export function parseSqlSignature(signature: string): { name: string, args: string[] | null } {
	const open = signature.indexOf('(');
	if (open < 0) return { name: signature.trim(), args: null };
	const close = signature.lastIndexOf(')');
	let inner = signature.substring(open + 1, close);
	// optional parts nest (`[, length [, decimal]]`): peel the innermost ones first
	for (let stripped = inner.replace(/\s*\[[^[\]]*\]/g, ''); stripped !== inner; stripped = inner.replace(/\s*\[[^[\]]*\]/g, '')) inner = stripped;
	return {
		name: signature.substring(0, open).trim(),
		args: inner.split(',').map(a => a.trim()).filter(Boolean),
	};
}

function functionSnippet(def: SqlFunctionDef): string {
	if (def[2]) return def[2];
	const { name, args } = parseSqlSignature(def[0]);
	if (args === null) return name;
	return name + '(' + args.map((arg, i) => '${' + (i + 1) + ':' + arg + '}') .join(', ') + ')';
}

function firstWord(text: string): string {
	return text.split(' ')[0];
}

/** The built-in items of the given groups in the given case. */
export function createSqlCompletionItems(
	letterCase: ResolvedCase,
	groups: Iterable<SqlCompletionGroup> = ALL_GROUPS,
	signatures = true,
	pseudoVariables: SqlPseudoVariable[] = SQL_PSEUDO_VARIABLES
): CodeCompletionItem[] {
	const enabled = new Set(groups);
	const items: CodeCompletionItem[] = [];

	if (enabled.has('keywords')) {
		for (const keyword of SQL_KEYWORDS) {
			const label = applySqlCase(keyword, letterCase);
			items.push({
				label,
				kind: 'keyword',
				filterText: label.includes(' ') ? firstWord(label) : undefined,
				sortText: SORT_PREFIX.keywords + label,
			});
		}
	}

	if (enabled.has('functions')) {
		for (const [category, defs] of Object.entries(SQL_FUNCTIONS)) {
			for (const def of defs) {
				const { name, args } = parseSqlSignature(def[0]);
				const label = applySqlCase(name, letterCase);
				const snippet = signatures && args !== null;
				items.push({
					label,
					kind: 'function',
					// the signature is written with an upper-case name and keywords, argument names lower-case
					detail: letterCase === 'lower' ? def[0].toLowerCase() : def[0],
					documentation: `**${category}**\n\n${def[1]}`,
					insertText: snippet ? applySqlCase(functionSnippet(def), letterCase) : label,
					snippet,
					sortText: SORT_PREFIX.functions + label,
				});
			}
		}
	}

	if (enabled.has('variables')) {
		for (const [name, description] of SQL_VARIABLES) {
			const label = applySqlCase(name, letterCase);
			items.push({ label, kind: 'variable', documentation: description, sortText: SORT_PREFIX.variables + label });
		}
	}

	// the engine's names are taken as written: the {placeholders} are replaced case-sensitively
	if (enabled.has('internal')) {
		for (const [name, type, description] of SQL_INTERNAL_VARIABLES) {
			items.push({ label: name, kind: 'variable', detail: type, documentation: description, sortText: SORT_PREFIX.internal + name });
		}
	}

	if (enabled.has('pseudo')) {
		for (const [name, description] of pseudoVariables) {
			items.push({ label: name, kind: 'constant', documentation: description, sortText: SORT_PREFIX.pseudo + name });
		}
	}

	if (enabled.has('macros')) {
		for (const [name, signature, description, snippet] of SQL_MACROS) {
			items.push({ label: name, kind: 'snippet', detail: signature, documentation: description, insertText: snippet, snippet: true, sortText: SORT_PREFIX.macros + name });
		}
	}

	if (enabled.has('types')) {
		for (const type of SQL_TYPES) {
			const label = applySqlCase(type, letterCase);
			items.push({ label, kind: 'keyword', detail: 'data type', sortText: SORT_PREFIX.types + label });
		}
	}

	if (enabled.has('snippets')) {
		for (const [name, description, snippet] of SQL_SNIPPETS) {
			const label = applySqlCase(name, letterCase);
			items.push({
				label,
				kind: 'snippet',
				documentation: description,
				insertText: applySqlCase(snippet, letterCase),
				snippet: true,
				filterText: firstWord(label),
				sortText: SORT_PREFIX.snippets + label,
			});
		}
	}

	return items;
}

export function createSqlCompletionProvider(options: SqlCompletionOptions = {}): CodeCompletionProvider {
	const disabled = new Set<SqlCompletionGroup>();
	if (options.internalVariables === false) disabled.add('internal');
	if (options.pseudoVariables === false) disabled.add('pseudo');
	if (options.macros === false) disabled.add('macros');
	const groups = (options.groups ?? ALL_GROUPS).filter(group => !disabled.has(group));
	const pseudoVariables = Array.isArray(options.pseudoVariables) ? options.pseudoVariables : SQL_PSEUDO_VARIABLES;
	const cache = new Map<ResolvedCase, CodeCompletionItem[]>();
	const builtin = (letterCase: ResolvedCase): CodeCompletionItem[] => {
		let items = cache.get(letterCase);
		if (!items) cache.set(letterCase, items = createSqlCompletionItems(letterCase, groups, options.signatures !== false, pseudoVariables));
		return items;
	};
	const schema = options.schema === false ? null : createSchemaCompletion(options.schema);

	return {
		triggerCharacters: [...new Set(['@', '{', ...(schema?.triggerCharacters ?? []), ...(options.triggerCharacters ?? [])])],
		documentWords: options.documentWords,
		async provide(context) {
			// behind a dot only the objects of the qualified name make sense, not keywords or variables
			const qualified = schema?.qualified(context);
			if (qualified) return qualified;

			const letterCase = resolveCase(options.keywordCase ?? 'auto', context.word);
			const extra = (typeof options.items === 'function' ? await options.items(context) : options.items) ?? [];
			let items = [
				...extra.map(item => item.sortText ? item : { ...item, sortText: SORT_PREFIX.extra + item.label }),
				...(schema?.unqualified(context) ?? []),
				...builtin(letterCase),
			];
			// after '@', '#', '{'... only items carrying that prefix can complete the token
			if (context.wordPrefix) items = items.filter(item => item.label.startsWith(context.wordPrefix));
			// the '{' (or '{{') was auto-closed by the editor: the closing braces after the cursor already belong to the item
			if (context.wordPrefix.endsWith('{') && context.lineSuffix.startsWith('}')) {
				const closing = /^}+/.exec(context.lineSuffix)?.[0].length ?? 0;
				items = items.map(item => {
					const text = item.insertText ?? item.label;
					const strip = Math.min(closing, /}*$/.exec(text)?.[0].length ?? 0);
					return strip ? { ...item, insertText: text.slice(0, -strip) } : item;
				});
			}
			return items;
		},
	};
}
